import {FunctionComponent, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useSearchParams} from 'react-router';
import AdvisorPredeterminedAllocations from '@/components/advisor-predetermined-allocations/advisor-predetermined-allocations';
import AdvisorProfileForm from '@/components/advisor-profile-form';
import ConfirmDialog from '@/components/confirm-dialog';
import {useConfirmDialogActions} from '@/components/confirm-dialog/use-confirm-dialog-actions';
import ContainerCard from '@/components/container-card';
import Message from '@/components/message';
import {Tab, TabGroup, TabList, TabPanel, TabPanels} from '@/components/tabs';
import {useUserProfileContext} from '@/contexts/user-profile';
import {useBlockNavigationIfFormDirty} from '@/hooks/use-block-navigation-if-form-dirty';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';

const TABS = ['profile', 'allocations'] as const;

const Profile: FunctionComponent = () => {
  const navigate = useNavigate();
  const {t} = useTranslation('common');
  const [searchParams] = useSearchParams();
  const {isVerified} = useUserProfileContext();

  const activeTabIndex = useMemo(() => {
    const activeTab = searchParams.get('tab') || TABS[0];
    return TABS.findIndex(tab => tab === activeTab);
  }, [searchParams]);

  const {setIsFormDirty, isNavigationBlocked, onConfirmNavigation, onCancelNavigation} =
    useBlockNavigationIfFormDirty();

  const onTabChange = useCallback((index: number) => navigate({search: `?tab=${TABS[index]}`}), [navigate]);

  const confirmDialogActions = useConfirmDialogActions({
    onCancel: onCancelNavigation,
    onConfirm: onConfirmNavigation,
  });

  return (
    <>
      <div className="flex h-fit grow flex-row gap-4">
        <ContainerCard className="flex size-full shrink grow-0 flex-col">
          <ContainerCard.Content className={'mt-0 pt-6'}>
            <TabGroup
              manual
              selectedIndex={activeTabIndex}
              onChange={onTabChange}
              data-testid="advisor-profile-content"
            >
              <div className="flex justify-center" data-testid="advisor-profile-tabs">
                <TabList>
                  <Tab data-testid="advisor-profile-tab" trackingKey={WEB_CLICK_TRACKING_KEY.VIEW_ADVISOR_PROFILE_TAB}>
                    <div className="relative inline-flex text-nowrap">
                      {t('routes.profile.profile.advisors-profile')}
                    </div>
                  </Tab>

                  <Tab
                    data-testid="advisor-allocations-tab"
                    trackingKey={WEB_CLICK_TRACKING_KEY.VIEW_PREDETERMINED_ALLOCATIONS_TAB}
                  >
                    <div className="relative inline-flex text-nowrap">
                      {t('routes.profile.profile.pre-determined-allocations')}
                    </div>
                  </Tab>
                </TabList>
              </div>

              <TabPanels>
                <TabPanel>
                  <AdvisorProfileForm data-testid="advisor-profile-form" onDirtyChange={setIsFormDirty} />
                </TabPanel>

                <TabPanel>
                  {!isVerified ? (
                    <>
                      <Message
                        text={t('components.advisor-profile-form.messages.different-field-detected')}
                        dataTestid="advisor-predetermined-allocations-verification-message"
                      />
                    </>
                  ) : (
                    <AdvisorPredeterminedAllocations data-testid="advisor-predetermined-allocations" />
                  )}
                </TabPanel>
              </TabPanels>
            </TabGroup>
          </ContainerCard.Content>
        </ContainerCard>
      </div>

      <ConfirmDialog
        isVisible={isNavigationBlocked}
        actions={confirmDialogActions}
        title={t('components.advisor-profile-form.navigation-confirm.title')}
        text={t('components.advisor-profile-form.navigation-confirm.text')}
      />
    </>
  );
};

export default Profile;
